import type { HomeyAPI, Experiment } from 'homey-api';
import { shallow } from 'zustand/shallow';

import { BaseApiStore, FetchArgs } from '../../../store/BaseApiStore.ts';

import { wait } from '../../../lib/wait';

import { useAttach } from '../../../store/useAttach';
import { selectBase } from '../../../store/selectors';

export interface State {
  experimentsData: ExperimentsData | null;
  isLoading: boolean;
  error: unknown;
}

type ExperimentsData = { [key: string]: Experiment };

export type ExperimentsSettingsStoreState = State;

class ExperimentsSettingsStore extends BaseApiStore<State> {
  constructor() {
    super('experiments-settings');
  }

  override createInitialState() {
    return {
      experimentsData: null,
      isLoading: true,
      error: null,
    };
  }

  override async fetch({ api, silent, cache, abortController }: FetchArgs) {
    if (silent === false) {
      this.set(this.createInitialState());
    }

    try {
      const time = silent === true ? 0 : 0;
      const waitPromise = wait(time);

      const experimentsPromise = api.experiments.getExperiments();

      const [, experimentsData] = await Promise.all([waitPromise, experimentsPromise]);

      if (abortController.signal.aborted) return;

      this.set({
        ...this.createInitialState(),
        experimentsData: experimentsData,
        isLoading: false,
      });
    } catch (error) {
      if (abortController.signal.aborted) return;

      this.set({
        ...this.createInitialState(),
        isLoading: false,
        error,
      });
    }
  }

  override destroy() {}

  onAttached({ api }: { api: HomeyAPI }): void {}

  onDetached({ api }: { api: HomeyAPI }): void {}

  use() {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useAttach(this, 'experiments-settings');

    return { experimentsSettings: this.store(selectAll, shallow) };
  }

  async enableExperiment({ id }: { id: string }) {
    const api = this.getApi();
    const result = await api.experiments.enableExperiment({ id });

    this.set((state) => {
      const currentData = state.experimentsData?.[id];
      // Somehow we have no data for this experiment.
      if (currentData == null) return {};

      return {
        experimentsData: {
          ...state.experimentsData,
          [id]: {
            ...currentData,
            enabled: true,
          },
        },
      };
    });

    return result;
  }

  async disableExperiment({ id }: { id: string }) {
    const api = this.getApi();
    const result = await api.experiments.disableExperiment({ id });

    this.set((state) => {
      const currentData = state.experimentsData?.[id];
      // Somehow we have no data for this experiment.
      if (currentData == null) return {};

      return {
        experimentsData: {
          ...state.experimentsData,
          [id]: {
            ...currentData,
            enabled: false,
          },
        },
      };
    });

    return result;
  }
}

function selectAll(state: ExperimentsSettingsStoreState) {
  return {
    ...selectBase(state),
    experimentsData: state.experimentsData,
  };
}

export const experimentsSettingsStore = new ExperimentsSettingsStore();
